import React, { useState, useEffect } from 'react';
import Card from '../components/Card';
import { apiClient } from '../services/api';
import { UsageMetrics } from '../types';
import './Usage.css';

const Usage: React.FC = () => {
  const [usage, setUsage] = useState<UsageMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      setLoading(true);
      const metrics = await apiClient.getUsage();
      setUsage(metrics);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch usage:', err);
      setError('Failed to load usage metrics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const calculatePercentage = (used: number, limit: number): number => {
    return Math.min((used / limit) * 100, 100);
  };

  const getProgressBarColor = (percentage: number): string => {
    if (percentage >= 90) return 'var(--color-error)';
    if (percentage >= 80) return 'var(--color-warning)';
    return 'var(--color-primary)';
  };

  const isApproachingLimit = (percentage: number): boolean => {
    return percentage >= 90;
  };

  if (loading) {
    return (
      <div className="usage-page">
        <div className="container">
          <h1>Usage Metrics</h1>
          <Card>
            <p className="text-center text-secondary">Loading usage metrics...</p>
          </Card>
        </div>
      </div>
    );
  }

  if (error || !usage) {
    return (
      <div className="usage-page">
        <div className="container">
          <h1>Usage Metrics</h1>
          <Card>
            <div className="alert alert-error">
              {error || 'Failed to load usage metrics'}
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const transcribePercentage = calculatePercentage(usage.transcribeMinutesUsed, usage.transcribeMinutesLimit);
  const translatePercentage = calculatePercentage(usage.translateCharsUsed, usage.translateCharsLimit);
  const storagePercentage = calculatePercentage(usage.s3StorageGB, usage.s3StorageLimit);

  const hasWarning = isApproachingLimit(transcribePercentage) || 
                     isApproachingLimit(translatePercentage) || 
                     isApproachingLimit(storagePercentage);

  return (
    <div className="usage-page">
      <div className="container">
        <h1>Usage Metrics</h1>
        
        {hasWarning && (
          <div className="alert alert-warning">
            <strong>Warning:</strong> One or more services are approaching their monthly limit (90% or more). 
            New job submissions may be disabled when limits are reached.
          </div>
        )}

        <Card>
          <h2>Monthly Usage</h2>
          <p className="text-secondary">
            Track your usage of AWS Free Tier services. Limits reset on the first day of each month.
          </p>

          <div className="usage-metrics">
            {/* Transcription Minutes */}
            <div className="metric-item">
              <div className="metric-header">
                <h3>Transcription Minutes</h3>
                <span className="metric-value">
                  {usage.transcribeMinutesUsed.toFixed(1)} / {usage.transcribeMinutesLimit.toFixed(0)} min
                </span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ 
                    width: `${transcribePercentage}%`,
                    backgroundColor: getProgressBarColor(transcribePercentage)
                  }}
                />
              </div>
              <div className="metric-footer">
                <span className="percentage">{transcribePercentage.toFixed(1)}% used</span>
                <span className="remaining">
                  {(usage.transcribeMinutesLimit - usage.transcribeMinutesUsed).toFixed(1)} min remaining
                </span>
              </div>
            </div>

            {/* Translation Characters */}
            <div className="metric-item">
              <div className="metric-header">
                <h3>Translation Characters</h3>
                <span className="metric-value">
                  {usage.translateCharsUsed.toLocaleString()} / {usage.translateCharsLimit.toLocaleString()} chars
                </span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ 
                    width: `${translatePercentage}%`,
                    backgroundColor: getProgressBarColor(translatePercentage)
                  }}
                />
              </div>
              <div className="metric-footer">
                <span className="percentage">{translatePercentage.toFixed(1)}% used</span>
                <span className="remaining">
                  {(usage.translateCharsLimit - usage.translateCharsUsed).toLocaleString()} chars remaining
                </span>
              </div>
            </div>

            {/* S3 Storage */}
            <div className="metric-item">
              <div className="metric-header">
                <h3>S3 Storage</h3>
                <span className="metric-value">
                  {usage.s3StorageGB.toFixed(2)} / {usage.s3StorageLimit.toFixed(0)} GB
                </span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ 
                    width: `${storagePercentage}%`,
                    backgroundColor: getProgressBarColor(storagePercentage)
                  }}
                />
              </div>
              <div className="metric-footer">
                <span className="percentage">{storagePercentage.toFixed(1)}% used</span>
                <span className="remaining">
                  {(usage.s3StorageLimit - usage.s3StorageGB).toFixed(2)} GB remaining
                </span>
              </div>
            </div>
          </div>

          <div className="usage-info">
            <p className="text-secondary">
              Last updated: {new Date(usage.lastUpdated).toLocaleString()}
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Usage;
